import { useMemo, useState } from 'react';
import {
  Button,
  DataTable,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type DataTableColumn,
  type DataTableSortState,
} from '@repo/ui-web';

type ShowcaseUser = {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'user';
};

const users: ShowcaseUser[] = [
  { id: '1', name: 'Ada Lovelace', email: 'ada@example.com', role: 'admin' },
  { id: '2', name: 'Grace Hopper', email: 'grace@example.com', role: 'user' },
  { id: '3', name: 'Linus Torvalds', email: 'linus@example.com', role: 'user' },
  { id: '4', name: 'Katherine Johnson', email: 'katherine@example.com', role: 'user' },
  { id: '5', name: 'Alan Turing', email: 'alan@example.com', role: 'admin' },
  { id: '6', name: 'Margaret Hamilton', email: 'margaret@example.com', role: 'user' },
  { id: '7', name: 'Barbara Liskov', email: 'barbara@example.com', role: 'user' },
];

const columns: DataTableColumn<ShowcaseUser>[] = [
  {
    id: 'name',
    header: 'Name',
    sortable: true,
    cell: (row) => <span className="font-medium">{row.name}</span>,
  },
  {
    id: 'email',
    header: 'Email',
    sortable: true,
    cell: (row) => row.email,
  },
  {
    id: 'role',
    header: 'Role',
    sortable: true,
    cell: (row) => row.role,
  },
  {
    id: 'actions',
    header: 'Actions',
    align: 'right',
    cell: () => (
      <Button type="button" variant="outline" size="sm">
        View
      </Button>
    ),
  },
];

function sortRows(rows: ShowcaseUser[], sort: DataTableSortState) {
  if (!sort) {
    return rows;
  }

  const sorted = [...rows];
  sorted.sort((left, right) => {
    const leftValue = String(left[sort.key as keyof ShowcaseUser] ?? '').toLowerCase();
    const rightValue = String(right[sort.key as keyof ShowcaseUser] ?? '').toLowerCase();
    const comparison = leftValue.localeCompare(rightValue);
    return sort.direction === 'asc' ? comparison : -comparison;
  });
  return sorted;
}

export function DataTableShowcase() {
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'user'>('all');
  const [sort, setSort] = useState<DataTableSortState>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const filteredRows = useMemo(
    () =>
      users.filter((row) => {
        const matchesQuery =
          query.trim().length === 0 ||
          row.name.toLowerCase().includes(query.toLowerCase()) ||
          row.email.toLowerCase().includes(query.toLowerCase());
        const matchesRole = roleFilter === 'all' || row.role === roleFilter;
        return matchesQuery && matchesRole;
      }),
    [query, roleFilter],
  );

  const sortedRows = useMemo(() => sortRows(filteredRows, sort), [filteredRows, sort]);
  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const pagedRows = sortedRows.slice(startIndex, startIndex + pageSize);
  const start = total === 0 ? 0 : startIndex + 1;
  const end = total === 0 ? 0 : Math.min(startIndex + pageSize, total);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={query}
          onChange={(event) => {
            setPage(1);
            setQuery(event.target.value);
          }}
          placeholder="Filter by name or email"
          className="w-64"
          showClearButton={false}
        />
        <Select
          value={roleFilter}
          onValueChange={(value) => {
            setPage(1);
            setRoleFilter(value as 'all' | 'admin' | 'user');
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="user">User</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        rows={pagedRows}
        columns={columns}
        getRowId={(row) => row.id}
        labels={{
          selectAllRows: 'Select all users on this page',
          selectRow: (row) => `Select ${row.email}`,
          emptyTitle: 'No users found',
          emptyDescription: 'Try a different search or filter.',
          pageSize: 'Page size',
          previous: 'Previous',
          next: 'Next',
          pageIndicator: (currentPage, maxPages) => `Page ${currentPage} of ${maxPages}`,
        }}
        page={safePage}
        totalPages={totalPages}
        pageSize={pageSize}
        pageSizeOptions={[5, 10, 20]}
        onPageChange={setPage}
        onPageSizeChange={(nextPageSize) => {
          setPage(1);
          setPageSize(nextPageSize);
        }}
        summary={`Showing ${start}-${end} of ${total}`}
        selectable
        selectedRowIds={selectedRowIds}
        onSelectedRowIdsChange={setSelectedRowIds}
        sort={sort}
        onSortChange={(nextSort) => {
          setPage(1);
          setSort(nextSort);
        }}
        rowVariant="separated"
        density="compact"
      />
    </div>
  );
}
